import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import RelatedNews from "@/components/feature/RelatedNews";
import ShareButton from "@/components/feature/ShareButton";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { formatChineseDate } from "@/utils/format";

export default function NewsDetailPage() {
  const { id } = useParams();
  const article = newsList.find((item) => item.id === id);

  useDocumentMeta({
    title: article ? `${article.title} - 张柏芝新闻资讯` : "资讯未找到 - 张柏芝新闻资讯",
    description: article
      ? `${article.summary} 本文来源：${article.source}，发布于 ${article.publishedAt}。`
      : "未找到该条张柏芝相关资讯，请返回新闻频道浏览其它报道。",
    keywords: article
      ? `张柏芝,${article.category},${article.title.slice(0, 12)},张柏芝新闻,明星资讯`
      : "张柏芝新闻,明星资讯",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!article) {
    return (
      <SiteLayout>
        <div className="flex w-full flex-col items-center justify-center px-4 py-32 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">没有找到这条资讯</h1>
          <p className="mt-2 text-sm text-foreground-600">链接可能已失效，或该条内容尚未归档。</p>
          <Link
            to="/news"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50"
          >
            返回新闻列表
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const relatedWorks = worksList.filter((work) => work.type === "电影").slice(0, 3);

  return (
    <SiteLayout>
      <article className="w-full">
        <div className="border-b border-background-200 bg-background-100 px-4 py-6 md:px-8 lg:px-12">
          <Breadcrumb items={[{ label: "新闻资讯", to: "/news" }, { label: article.category, to: "/news" }, { label: "正文" }]} />
        </div>

        <div className="grid grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-[1fr_320px] lg:px-12">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
              <Link
                to="/news"
                className="rounded-sm bg-primary-100 px-2 py-0.5 text-primary-800 transition-colors hover:bg-primary-200"
              >
                {article.category}
              </Link>
              <span>{formatChineseDate(article.publishedAt)}</span>
              <span className="text-foreground-300">·</span>
              <span>来源：{article.source}</span>
              <span className="text-foreground-300">·</span>
              <span>热度 {article.heat}</span>
            </div>

            <h1 className="mt-4 font-heading text-[24px] font-semibold leading-snug text-foreground-950 md:text-[34px] md:leading-tight">
              {article.title}
            </h1>

            <p className="mt-4 border-l-2 border-primary-400 pl-4 text-sm leading-7 text-foreground-600">
              {article.summary}
            </p>

            <figure className="mt-7">
              <div className="h-[240px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
                <img
                  src={article.cover}
                  alt={`${article.title} - 张柏芝新闻封面大图`}
                  title={`${article.title} - 张柏芝新闻`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-[11px] text-foreground-500">
                图：{article.source}（示范内容，仅用于展示页面效果）
              </figcaption>
            </figure>

            <div className="mt-8 space-y-5">
              {article.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 12)} className="text-[15px] leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-9 flex flex-col gap-4 rounded-lg bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-600">所属分类</span>
                <Link
                  to="/news"
                  className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  {article.category}
                </Link>
                <Link
                  to="/news"
                  className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  全部新闻
                </Link>
              </div>
              <ShareButton title={article.title} />
            </div>

            <section aria-label="相关作品" className="mt-8 rounded-lg border border-background-200 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-film-line text-sm" aria-hidden="true"></i>
                </span>
                文中相关作品
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {relatedWorks.map((work) => (
                  <Link
                    key={work.id}
                    to={`/works/${work.id}`}
                    className="group overflow-hidden rounded-md border border-background-200 transition-colors hover:border-primary-300"
                  >
                    <div className="h-32 w-full overflow-hidden bg-background-200">
                      <img
                        src={work.cover}
                        alt={`${work.title} 海报`}
                        title={`${work.title} - 张柏芝影视作品`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <p className="truncate text-xs text-foreground-900 transition-colors group-hover:text-primary-700">
                        {work.title}
                      </p>
                      <p className="mt-0.5 text-[11px] text-foreground-500">
                        {work.year} · {work.type}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
            <RelatedNews currentId={article.id} category={article.category} />
            <div className="rounded-lg bg-secondary-100 p-5">
              <h2 className="font-heading text-sm font-semibold text-foreground-950">站内导航</h2>
              <ul className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { label: "新闻频道", to: "/news", icon: "ri-newspaper-line" },
                  { label: "影视作品", to: "/works", icon: "ri-film-line" },
                  { label: "写真图集", to: "/gallery", icon: "ri-image-line" },
                  { label: "特别专题", to: "/special", icon: "ri-bookmark-line" },
                ].map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="flex items-center gap-2 rounded-md bg-background-50 px-3 py-2 text-xs text-foreground-800 transition-colors hover:text-primary-700"
                    >
                      <i className={`${item.icon} text-primary-600`} aria-hidden="true"></i>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </article>
    </SiteLayout>
  );
}